/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.nextUrl = this.dataset.nextUrl || '';
      this.loadedUrls = new Set();
      this.abortController = null;
      this.scrollTicking = false;
      this.scrollIntent = 0;
      this.consumedScrollIntent = 0;
      this.restoreRequested = false;
      this.restoringScrollState = false;
      this.scrollRestoreCancelled = false;
      this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onUserScrollIntent = this.onUserScrollIntent.bind(this);
      this.onWindowScroll = this.onWindowScroll.bind(this);
      this.saveScrollState = this.saveScrollState.bind(this);
    }

    connectedCallback() {
      this.grid = document.querySelector(`[data-id="${this.sectionId}"]`);

      if (!this.grid) {
        this.finish();
        return;
      }

      this.grid.addEventListener('click', this.onGridClick);
      window.addEventListener('pagehide', this.saveScrollState);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('wheel', this.onUserScrollIntent, { passive: true });
      window.addEventListener('touchmove', this.onUserScrollIntent, { passive: true });
      window.addEventListener('keydown', this.onUserScrollIntent);

      if (this.classList.contains('pagination-type--loadmore') && this.button) {
        this.button.addEventListener('click', this.onLoadMoreClick);
      }

      this.restoreScrollState().finally(() => {
        if (this.classList.contains('pagination-type--infinite') && this.nextUrl && this.isConnected) {
          this.initInfiniteScroll();
        }
      });
    }

    disconnectedCallback() {
      if (this.grid) {
        this.grid.removeEventListener('click', this.onGridClick);
      }
      window.removeEventListener('pagehide', this.saveScrollState);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('scroll', this.onWindowScroll);
      window.removeEventListener('wheel', this.onUserScrollIntent);
      window.removeEventListener('touchmove', this.onUserScrollIntent);
      window.removeEventListener('keydown', this.onUserScrollIntent);
      if (this.button) {
        this.button.removeEventListener('click', this.onLoadMoreClick);
      }
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    onLoadMoreClick() {
      this.loadProducts();
      this.button.blur();
    }

    get scrollStateKey() {
      return `collection-scroll:${window.location.pathname}${window.location.search}`;
    }

    get historyScrollStateKey() {
      return 'bloomrCollectionScrollKey';
    }

    hasHistoryScrollState() {
      const state = window.history.state;
      return Boolean(
        state
        && typeof state === 'object'
        && state[this.historyScrollStateKey] === this.scrollStateKey
      );
    }

    updateHistoryScrollState(restoreRequested) {
      try {
        const currentState = window.history.state;
        const state = currentState && typeof currentState === 'object'
          ? { ...currentState }
          : {};

        if (restoreRequested) {
          state[this.historyScrollStateKey] = this.scrollStateKey;
        } else {
          delete state[this.historyScrollStateKey];
        }

        window.history.replaceState(state, document.title);
      } catch (error) {
        // History state can be unavailable in embedded browsers.
      }
    }

    getScrollState() {
      try {
        const state = JSON.parse(window.sessionStorage.getItem(this.scrollStateKey));
        const isFresh = state && Date.now() - state.timestamp < 30 * 60 * 1000;
        return isFresh
          && state.sectionId === this.sectionId
          && state.restoreRequested === true
          && this.hasHistoryScrollState()
          ? state
          : null;
      } catch (error) {
        return null;
      }
    }

    saveScrollState() {
      if (!this.grid || !this.restoreRequested) return;

      try {
        window.sessionStorage.setItem(this.scrollStateKey, JSON.stringify({
          sectionId: this.sectionId,
          scrollY: Math.max(0, window.scrollY),
          loadedPageCount: this.loadedUrls.size,
          restoreRequested: true,
          timestamp: Date.now()
        }));
      } catch (error) {
        // Storage can be unavailable in private browsing; navigation should still work.
      }
    }

    onGridClick(event) {
      const link = event.target.closest('a[href]');
      const opensInCurrentTab = link
        && !event.defaultPrevented
        && !event.metaKey
        && !event.ctrlKey
        && !event.shiftKey
        && !event.altKey
        && (!link.target || link.target === '_self');

      if (!opensInCurrentTab || !link.closest('product-card')) return;

      this.restoreRequested = true;
      this.updateHistoryScrollState(true);
      this.saveScrollState();
    }

    onPageShow(event) {
      if (!event.persisted) return;

      // A bfcache return keeps both the appended grid and its exact scroll offset.
      // A manual scrollTo here can fight Safari's native restoration.
      this.clearScrollState();
    }

    clearScrollState() {
      this.restoreRequested = false;
      this.updateHistoryScrollState(false);

      try {
        window.sessionStorage.removeItem(this.scrollStateKey);
      } catch (error) {
        // Storage can be unavailable in private browsing.
      }
    }

    async restoreScrollState() {
      const state = this.getScrollState();
      if (!state) return;

      this.restoringScrollState = true;
      this.scrollRestoreCancelled = false;
      this.clearScrollState();

      try {
        const pageCount = Math.max(0, Number(state.loadedPageCount) || 0);
        for (let index = 0; index < pageCount && this.nextUrl; index += 1) {
          const loaded = await this.loadProducts();
          if (!loaded) break;
        }

        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              if (!this.scrollRestoreCancelled && this.isConnected) {
                window.scrollTo(0, state.scrollY);
              }
              resolve();
            });
          });
        });
      } finally {
        this.restoringScrollState = false;
      }
    }

    initInfiniteScroll() {
      if (this.observer) return;

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.requestInfiniteLoad();
        }
      }, {
        rootMargin: '500px 0px',
        threshold: 0.01
      });
      this.observer.observe(this);
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      window.addEventListener('wheel', this.onUserScrollIntent, { passive: true });
      window.addEventListener('touchmove', this.onUserScrollIntent, { passive: true });
      window.addEventListener('keydown', this.onUserScrollIntent);
      this.checkInfinitePosition();
    }

    onUserScrollIntent(event) {
      const scrollKeys = ['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' '];
      if (
        event.type === 'keydown'
        && !scrollKeys.includes(event.key)
      ) {
        return;
      }

      if (this.restoringScrollState) {
        this.scrollRestoreCancelled = true;
      }

      if (
        event.type !== 'keydown'
        || ['ArrowDown', 'End', 'PageDown', ' '].includes(event.key)
      ) {
        this.scrollIntent += 1;
      }
    }

    onWindowScroll() {
      if (this.scrollTicking) return;

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.scrollTicking = false;
        this.checkInfinitePosition();
      });
    }

    checkInfinitePosition() {
      if (!this.nextUrl || !this.isConnected || this.hasAttribute('loading')) return;

      const paginationTop = this.getBoundingClientRect().top;
      if (paginationTop <= window.innerHeight + 500) {
        this.requestInfiniteLoad();
      }
    }

    requestInfiniteLoad() {
      if (this.scrollIntent <= this.consumedScrollIntent) return;

      this.consumedScrollIntent = this.scrollIntent;
      this.loadProducts();
    }

    async loadProducts() {
      const url = this.nextUrl;
      if (!url || this.hasAttribute('loading') || this.loadedUrls.has(url)) return false;

      this.loadedUrls.add(url);
      this.setAttribute('loading', 'true');
      this.removeAttribute('data-load-error');
      let rendered = false;

      if (this.observer) {
        this.observer.unobserve(this);
      }

      this.abortController = new AbortController();

      try {
        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: 'text/html' }
        });

        if (!response.ok) {
          throw new Error(`Pagination request failed with status ${response.status}`);
        }

        const responseText = await response.text();
        const result = this.renderProducts(responseText);

        if (!result.rendered) {
          throw new Error('Pagination response did not contain a usable product grid');
        }

        rendered = true;
        this.nextUrl = result.nextUrl;
        this.dataset.nextUrl = result.nextUrl;

        const count = result.document.getElementById('ProductCount');
        const currentCount = document.getElementById('ProductCount');
        if (count && currentCount) {
          currentCount.innerHTML = count.innerHTML;
        }

        dispatchCustomEvent('pagination:page-changed', { url });

        if (!this.nextUrl) {
          this.finish();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.loadedUrls.delete(url);
          this.setAttribute('data-load-error', 'true');
          console.error('Error loading products:', error);
        }
      } finally {
        this.abortController = null;
        this.removeAttribute('loading');

        if (this.observer && this.nextUrl && this.isConnected) {
          this.observer.observe(this);
        }
      }

      return rendered;
    }

    renderProducts(html) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = parsedDocument.getElementById('product-grid');

      if (!incomingGrid || incomingGrid.dataset.id !== this.sectionId) {
        return { rendered: false, nextUrl: '', document: parsedDocument };
      }

      const products = Array.from(incomingGrid.children).filter((item) => {
        return item.classList.contains('columns') && !item.classList.contains('loading-overlay');
      });

      if (!products.length) {
        return { rendered: false, nextUrl: '', document: parsedDocument };
      }

      const existingKeys = new Set(
        Array.from(this.grid.children)
          .map((item) => item.dataset.paginationKey)
          .filter(Boolean)
      );
      const productsToAppend = products.filter((product) => {
        const key = product.dataset.paginationKey;
        if (!key) return true;
        if (existingKeys.has(key)) return false;

        existingKeys.add(key);
        return true;
      });

      const fragment = document.createDocumentFragment();
      productsToAppend.forEach((product) => fragment.appendChild(product));
      this.grid.appendChild(fragment);

      if (
        window.lazySizes
        && window.lazySizes.loader
        && typeof window.lazySizes.loader.checkElems === 'function'
      ) {
        window.lazySizes.loader.checkElems();
      }

      const nextPagination = parsedDocument.querySelector(
        `pagination-theme[data-section="${this.sectionId}"]`
      );

      return {
        rendered: true,
        nextUrl: nextPagination ? nextPagination.dataset.nextUrl || '' : '',
        document: parsedDocument
      };
    }

    finish() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      if (this.observer) {
        this.observer.disconnect();
      }
      window.removeEventListener('scroll', this.onWindowScroll);
      window.removeEventListener('wheel', this.onUserScrollIntent);
      window.removeEventListener('touchmove', this.onUserScrollIntent);
      window.removeEventListener('keydown', this.onUserScrollIntent);
      if (this.button) {
        this.button.classList.add('visually-hidden');
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}
